import {useEffect, useMemo, useState, useCallback} from 'react'
import DataTableList from '../../components/DataTableList'
import axios from 'axios'
import {API_ENDPOINTS} from '../../../../constants/apiConstants'
import {ADMIN_ALL_DEVICES_HEADERS} from '../../../../constants/RoleConstants'
import {DeviceTypeModel} from '../../../../entities/device.entity'
import {ActionDefaults} from '../../modules/accounts/components/CRUDWrapper/DefaultActions'
import AssignDeviceModal from '../../components/modals/AssignDeviceModal'
import {toast} from 'react-toastify'
import CreateDeviceModal from '../../components/modals/CreateDeviceModal'
import {CreateDeviceFormValuesModel} from '../../../../entities/component.entity'
import EditDeviceModal from '../../components/modals/EditDeviceModal'
import MetaTags from '../../../../meta/MetaTags'
import Loader from '../../components/Loader'
import {FaTimesCircle} from 'react-icons/fa'
import PrimaryButton from '../../components/common/buttons/PrimaryButton'
import {Modal} from 'react-bootstrap'

const actions = [ActionDefaults.assignDevice, ActionDefaults.edit]

// This is just to display the super admin
const AllAvailableDevicesListingPage = () => {
  const [devices, setDevices] = useState([])
  const [doctorsList, setDoctorsList] = useState([])
  const [isAssignDeviceModalOpen, setIsAssignDeviceModalOpen] = useState(false)
  const [isAssignDeviceLoading, setIsAssignDeviceLoading] = useState(false)
  const [selectedDevice, setSelectedDevice] = useState<any>(null)
  const [isCreateDeviceModalOpen, setIsCreateDeviceModalOpen] = useState(false)
  const [deviceTypes, setDeviceTypes] = useState<DeviceTypeModel[]>([]) // Type-safe array
  const [isEditDeviceModalOpen, setIsEditDeviceModalOpen] = useState(false)
  const [selectedEditDevice, setSelectedEditDevice] = useState<CreateDeviceFormValuesModel | null>(
    null
  )
  const [uploadReport, setUploadReport] = useState<any>(null)
  const [isDeviceCreationLoading, setIsDeviceCreationLoading] = useState(false)
  const [isCSVUploading, setIsCSVUploading] = useState(false)
  const [isEditDeviceLoading, setIsEditDeviceLoading] = useState(false)
  // Form dirty state tracking
  const [formDirty, setFormDirty] = useState(false)
  const [showDiscardModal, setShowDiscardModal] = useState(false)

  // Create a memoized refreshDevices function that can be passed to components
  const refreshDevices = useCallback(() => {
    fetchDevices()
  }, [])

  useEffect(() => {
    const fetchDeviceTypes = async () => {
      try {
        const response = await axios.get(API_ENDPOINTS.GET_ALL_DEVICE_TYPES())
        // Check both possible response structures
        const deviceData = response.data?.data?.device_types || response.data?.data?.devices || response.data?.data || []
        const formattedDeviceTypes: DeviceTypeModel[] = deviceData.map((device: any) => ({
          id: device.id,
          name: device.name,
        }))
        setDeviceTypes(formattedDeviceTypes)
      } catch (error) {
        console.error('Failed to fetch device types:', error)
      }
    }

    fetchDevices()
    fetchDoctors()
    fetchDeviceTypes()
  }, [])

  // handle actions function
  const handleActionPerformed = async (actionType: any, row: any) => {
    // setSelectedItem(row)
    // console.log()
    switch (actionType.name) {
      case 'ASSIGN_DEVICE':
        setSelectedDevice(row) // Store the selected device
        setIsAssignDeviceModalOpen(true)
        break

      case 'EDIT':
        // Find the active allocation (where returned_at is null)
        const activeAllocation = row.DoctorAllocatedDevices?.find(
          (allocation: any) => allocation.returned_at === null
        )
        
        const formattedDevice: CreateDeviceFormValuesModel = {
          id: row.id, // Include the ID if needed
          deviceType: String(row.device_type_id), // Convert to string if needed for <select>
          imeiNumber: row.imei || '',
          name: row.name || '',
          modelNumber: row.model_no || '',
          serialNumber: row.sr_no || '',
          description: row.description || '',
          price: row.price || '', // Ensure it's a number or empty string
          ownershipType: activeAllocation?.ownership_type || 'purchased',
        }
        setSelectedEditDevice(formattedDevice) // Store formatted device details
        setIsEditDeviceModalOpen(true) // Open modal
        break

      default:
        console.log('')
    }
  }

  const fetchDevices = async () => {
    try {
      const response = await axios.get(API_ENDPOINTS.GET_ALL_DEVICES())
      setDevices(response.data?.data?.devices || [])
    } catch (error) {
      console.error('Failed to fetch devices:', error)
      toast.error('Failed to fetch devices')
    }
  }

  const fetchDoctors = async () => {
    try {
      const response = await axios.get(API_ENDPOINTS.GET_ALL_DOCTORS())
      setDoctorsList(response?.data?.data || [])
    } catch (error) {
      console.error('Failed to fetch doctors:', error)
    }
  }
  // Handle Add Click to open the modal
  const handleAddClick = () => {
    setIsCreateDeviceModalOpen(true)
    setFormDirty(false)
    setShowDiscardModal(false)
  }

  // For Assigning Device
  const handleFormSubmit = async (values: any, resetForm: () => void) => {
    setIsAssignDeviceLoading(true)
    try {
      const doctorId = values?.id
      const deviceId = values?.device_id
      const ownershipType = values?.ownershipType

      const response = await axios.post(API_ENDPOINTS.ASSIGN_DEVICE_TO_DOCTOR(), {
        doctor_id: doctorId,
        device_id: deviceId,
        ownership_type: ownershipType,
      })

      toast.success(response?.data?.message)
      fetchDevices()
      fetchDoctors()
    } catch (error) {
      if (axios.isAxiosError(error)) {
        toast.error(error.response?.data?.message || 'Something went wrong')
      } else if (error instanceof Error) {
        toast.error(error.message)
      } else {
        toast.error('An unexpected error occurred')
      }
    } finally {
      setIsAssignDeviceModalOpen(false)
      setIsAssignDeviceLoading(false)
      setSelectedDevice(null)
      resetForm()
    }
  }

  // For Creating New Device
  const handleCreateSubmit = async (values: CreateDeviceFormValuesModel, resetForm: () => void) => {
    setIsDeviceCreationLoading(true)
    try {
      const createDeviceBody = {
        // description: values?.description,
        device_type_id: +values?.deviceType,
        imei: values?.imeiNumber,
        model_no: values?.modelNumber,
        ownership_type: values?.ownershipType,
        sr_no: values?.serialNumber || null,
        // price: values?.price,
        name: values?.name,
      }
      const response = await axios.post(API_ENDPOINTS.CREATE_DEVICE(), createDeviceBody)
      if (response.data.status === 'Success') {
        toast.success(response.data.message)
        resetForm()
        fetchDevices()
        fetchDoctors()
        setFormDirty(false)
      } else {
        toast.error(response.data.message || 'Failed to submit device request.')
      }
      setIsCreateDeviceModalOpen(false)
    } catch (error) {
      // Todo Refactor this
      if (axios.isAxiosError(error)) {
        toast.error(error.response?.data?.message || 'Something went wrong')
      } else if (error instanceof Error) {
        toast.error(error.message)
      } else {
        toast.error('An unexpected error occurred')
      }
    } finally {
      setIsDeviceCreationLoading(false)
    }
  }

  // For Editing A selected device
  const handleEditSubmit = async (values: CreateDeviceFormValuesModel, resetForm: () => void) => {
    setIsEditDeviceLoading(true)
    try {
      const editDeviceBody = {
        description: values.description,
        device_type_id: +values.deviceType,
        imei: values.imeiNumber,
        model_no: values.modelNumber,
        ownership_type: values.ownershipType,
        price: values.price,
        sr_no: values.serialNumber || null,
        name: values.name,
      }

      const response = await axios.put(
        API_ENDPOINTS.UPDATE_DEVICE(selectedEditDevice?.id!),
        editDeviceBody
      )

      if (response.data.status === 'Success') {
        toast.success(response.data.message)
        resetForm()
        setIsEditDeviceModalOpen(false) // Close modal
        fetchDevices()
        fetchDoctors()
      } else {
        toast.error(response.data.message || 'Failed to update device.')
      }
    } catch (error) {
      if (axios.isAxiosError(error)) {
        toast.error(error.response?.data?.message || 'Something went wrong')
      } else {
        toast.error('An unexpected error occurred')
      }
    } finally {
      setIsEditDeviceLoading(false)
    }
  }

  // creating devices from csv
  const handleCSVUpload = async (csvFile: any) => {
    setUploadReport(null)
    if (!csvFile) return toast.error('Please select a CSV file.')

    const formData = new FormData()
    formData.append('file', csvFile)

    setIsCSVUploading(true)
    try {
      const res = await axios.post(API_ENDPOINTS.UPLOAD_DEVICE_CSV(), formData)
      setUploadReport(res.data.data)
      toast.success(res.data.message)
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Upload failed')
    } finally {
      setIsCSVUploading(false)
    }
  }
  // search filters from datatables
  const [searchText, setSearchText] = useState('')
  const [ownershipFilter, setOwnershipFilter] = useState('')
  const filteredData = useMemo(() => {
    return devices.filter((device: any) => {
      // Search text filter
      const searchFilter =
        searchText === '' ||
        device.name?.toLowerCase().includes(searchText.toLowerCase()) ||
        device.imei?.toLowerCase().includes(searchText.toLowerCase()) ||
        device.sr_no?.toLowerCase().includes(searchText.toLowerCase()) ||
        // Check ALL allocations, not just the first one
        device.DoctorAllocatedDevices?.some((allocation: any) =>
          allocation.User?.full_name?.toLowerCase().includes(searchText.toLowerCase())
        )

      // Ownership filter
      let ownershipFilterMatch = true
      if (ownershipFilter !== '') {
        const activeAllocation = device.DoctorAllocatedDevices?.find(
          (allocation: any) => allocation.returned_at === null
        )
        const statusMap: Record<string, string> = {
          rental: 'Rented',
          purchased: 'Owned',
          loaned: 'Loaned',
        }
        const ownershipType = activeAllocation?.ownership_type || 'purchased'
        const deviceOwnership = activeAllocation
          ? (statusMap[ownershipType] || 'Owned')
          : 'Not Allocated'
        ownershipFilterMatch = deviceOwnership === ownershipFilter
      }

      return searchFilter && ownershipFilterMatch
    })
  }, [devices, searchText, ownershipFilter])
  const handleFilterChange = (key: string, value: string) => {
    switch (key) {
      case 'search':
        setSearchText(value)
        break
      case 'ownership':
        setOwnershipFilter(value)
        break
      default:
        break
    }
  }
  const handleClearAllFilters = () => {
    setSearchText('')
    setOwnershipFilter('')
  }

  const customFilters = [
    {
      name: 'Search',
      key: 'search',
      type: 'search' as const,
      placeholder: 'Search by name, IMEI, Sr No, or allocated user',
      value: searchText,
    },
    {
      name: 'Ownership',
      key: 'ownership',
      type: 'select' as const,
      placeholder: 'All Ownership',
      value: ownershipFilter,
      options: [
        {label: 'All Ownership', value: ''},
        {label: 'Loaned', value: 'Loaned'},
        {label: 'Rented', value: 'Rented'},
        {label: 'Owned', value: 'Owned'},
        {label: 'Not Allocated', value: 'Not Allocated'},
      ],
    },
  ]
  const closeModal = () => {
    // Check if form is dirty before closing
    if (formDirty) {
      setShowDiscardModal(true)
    } else {
      setIsCreateDeviceModalOpen(false)
      // reset upload count on modal close
      setUploadReport(null)
      setFormDirty(false)
    }
  }

  // Handle modal close with confirmation
  const handleModalClose = () => {
    if (formDirty) {
      setShowDiscardModal(true)
    } else {
      setIsCreateDeviceModalOpen(false)
      setFormDirty(false)
      setUploadReport(null)
    }
  }

  // Keep editing the form
  const keepEditing = () => {
    setShowDiscardModal(false)
  }

  // Discard changes and close the form
  const discardChanges = () => {
    setShowDiscardModal(false)
    setIsCreateDeviceModalOpen(false)
    setFormDirty(false)
    setUploadReport(null)
  }

  return (
    <>
      <MetaTags title={'Device List'} />

      {isAssignDeviceModalOpen && (
        <AssignDeviceModal
          isOpen={isAssignDeviceModalOpen}
          onClose={() => setIsAssignDeviceModalOpen(false)}
          handleSubmit={handleFormSubmit}
          selectedDevice={selectedDevice} // Pass the selected device
          doctorsList={doctorsList}
          isAssignDeviceLoading={isAssignDeviceLoading}
        />
      )}

      {isCreateDeviceModalOpen && (
        <div className={showDiscardModal ? 'd-none' : ''}>
          <CreateDeviceModal
            isOpen={isCreateDeviceModalOpen}
            onClose={closeModal}
            handleSubmit={handleCreateSubmit}
            deviceTypes={deviceTypes}
            handleCSVUpload={(csvFile: any) => handleCSVUpload(csvFile)}
            uploadReport={uploadReport}
            isDeviceCreationLoading={isDeviceCreationLoading}
            isCSVUploading={isCSVUploading}
            setFormDirty={setFormDirty}
            formDirty={formDirty}
            showDiscardModal={showDiscardModal}
            handleModalClose={handleModalClose}
            discardChanges={discardChanges}
            keepEditing={keepEditing}
          />
        </div>
      )}

      {/* Add Edit Device Modal here */}
      {isEditDeviceModalOpen && selectedEditDevice && (
        <EditDeviceModal
          isOpen={isEditDeviceModalOpen}
          onClose={() => setIsEditDeviceModalOpen(false)}
          handleSubmit={handleEditSubmit}
          initialValues={selectedEditDevice} // Pre-fill form
          deviceTypes={deviceTypes} // Pass available device types
          isLoading={isEditDeviceLoading}
        />
      )}
      {isAssignDeviceLoading || isDeviceCreationLoading || isEditDeviceLoading ? (
        <Loader />
      ) : (
        <>
          <DataTableList
            title={'Device List'}
            columns={ADMIN_ALL_DEVICES_HEADERS}
            data={filteredData}
            actions={actions}
            onAction={handleActionPerformed}
            onAddClick={handleAddClick}
            addButtonText='Add Device'
            showOnlySearch={true}
            showCustomFilters={true}
            customFilters={customFilters}
            onCustomFilterChange={handleFilterChange}
            onClearAllCustomFilters={handleClearAllFilters}
            onReload={refreshDevices}
            customInputWidth="300px"
          />
        </>
      )}

      {/* Discard changes modal */}
      <Modal show={showDiscardModal} onHide={keepEditing} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <FaTimesCircle className='text-danger me-2' />
            Unsaved Changes
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          You have entered information that hasn't been saved yet. Are you sure you want to discard
          your changes?
        </Modal.Body>
        <Modal.Footer>
          <PrimaryButton color='secondary' onClick={keepEditing}>
            Keep Editing
          </PrimaryButton>
          <PrimaryButton color='danger' onClick={discardChanges}>
            Discard Changes
          </PrimaryButton>
        </Modal.Footer>
      </Modal>
    </>
  )
}

export default AllAvailableDevicesListingPage
